import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Package } from "@phosphor-icons/react";

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      onLogin(res.data);
      toast.success("Signed in");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative overflow-hidden" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1557761469-f29c6e201784?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODQwNDMyMjN8MA&ixlib=rb-4.1.0&q=85')",
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        <div className="absolute inset-0 bg-zinc-950/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <Package size={28} weight="bold" />
            <span className="font-mono text-sm tracking-widest uppercase">COURIER ERP</span>
          </div>
          <div>
            <h1 className="text-5xl font-black tracking-tighter mb-4">International<br/>Courier Operations</h1>
            <p className="text-sm text-white/70 max-w-md leading-relaxed">
              Manage orders, payments, receivers, and shipments across Europe and India from one command center.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Sign In</p>
            <h2 className="text-3xl font-bold tracking-tight">Welcome back</h2>
          </div>

          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" required
                className="mt-2 border-zinc-300 focus:border-zinc-900 focus:ring-0 rounded-md"
                data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-500">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required
                className="mt-2 border-zinc-300 focus:border-zinc-900 focus:ring-0 rounded-md"
                data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-md py-6 transition-colors"
              data-testid="login-submit-button">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-zinc-200" />
            <span className="text-xs uppercase tracking-widest text-zinc-400">or</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>

          <Button onClick={handleGoogleLogin} type="button" variant="outline"
            className="w-full border-zinc-300 hover:bg-zinc-50 rounded-md py-6 transition-colors"
            data-testid="google-login-button">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>

          <p className="text-xs text-zinc-500 mt-8 leading-relaxed">
            Secure sign-in. Sessions are protected by HttpOnly cookies. Role-based access enforced on all operations.
          </p>
        </div>
      </div>
    </div>
  );
}
